<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { selectedCountry } from '../countryStore';
  import { getRelativeTime } from '../utils'; // Import helper
  import ReportModal from '../components/ReportModal.svelte'; // Import Report Modal
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  let productName = '';
  let storeName = '';
  let barcode = '';

  let results = [];
  let isLoading = false;
  let error = '';
  let currentUser = null;
  
  // Pagination State
  let page = 1;
  const pageSize = 10;
  let hasMore = false;

  // Image Modal State
  let showImageModal = false;
  let selectedImageUrl = '';
  let selectedImageName = '';
  
  // Report Modal State
  let showReportModal = false;
  let reportTargetId = '';

  authStore.subscribe(value => {
    currentUser = value.user;
  });
  
  onMount(() => {
      handleSearch(true);
  });
  
  $: if ($selectedCountry) {
      handleSearch(true);
  }

  async function handleSearch(reset = false) {
    if (reset) {
        page = 1;
        results = [];
        hasMore = false;
    }
    
    isLoading = true;
    error = '';
    
    const params = new URLSearchParams();
    if (productName) params.append("productName", productName);
    if (storeName) params.append("storeName", storeName);
    if (barcode) params.append("barcode", barcode);
    params.append("countryCode", $selectedCountry);
    params.append("page", page);
    params.append("pageSize", pageSize);
    
    try {
      const response = await fetch(getApiUrl(`/products/search?${params.toString()}`));
      if (!response.ok) throw new Error('Search failed.');
      
      const responseData = await response.json();
      const newProducts = responseData.data || [];
      const totalPages = responseData.totalPages || 1;

      if (reset) {
          results = newProducts;
      } else {
          results = [...results, ...newProducts];
      }
      
      hasMore = page < totalPages;

    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function loadMore() {
      page += 1;
      handleSearch(false);
  }

  async function handleDelete(id) {
    if (!confirm("Are you sure you want to delete this product discount?")) return;
    try {
        const userId = currentUser?.idTokenClaims?.oid;
        const response = await authFetch(getApiUrl(`/products/${id}?userId=${userId}`), { method: 'DELETE' });
        if (!response.ok) { const data = await response.json(); throw new Error(data.error || "Failed to delete."); }
        results = results.filter(item => item._id !== id);
        alert("Product deleted.");
    } catch (e) { alert(e.message); }
  }

  async function handleLike(id) {
    if (!currentUser) { alert("Please log in to like products."); return; }
    try {
        const userId = currentUser.idTokenClaims.oid;
        const response = await authFetch(getApiUrl(`/products/${id}/like`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
        });
        if (response.ok) {
            const index = results.findIndex(p => p._id === id);
            if (index !== -1) { results[index].likeCount = (results[index].likeCount || 0) + 1; }
        }
    } catch (e) { console.error("Like failed", e); }
  }

  // --- Image Modal Handlers ---
  function openImageModal(url, name) {
      selectedImageUrl = url;
      selectedImageName = name;
      showImageModal = true;
  }

  function closeImageModal() {
      showImageModal = false;
      selectedImageUrl = '';
      selectedImageName = '';
  }
  
  // --- Report Logic ---
  function openReportModal(id) {
      if(!currentUser) { alert("Please log in."); return; }
      reportTargetId = id;
      showReportModal = true;
  }

  function handleReportSuccess() {
      const index = results.findIndex(p => p._id === reportTargetId);
      if (index !== -1) {
          results[index].issueCount = (results[index].issueCount || 0) + 1;
      }
      alert("Report submitted.");
  }
</script>

<!-- Image Preview Modal -->
{#if showImageModal}
  <div class="modal-backdrop" on:click={closeImageModal}>
    <div class="modal-content image-modal-content" on:click|stopPropagation>
      <button class="close-modal-btn" on:click={closeImageModal}>&times;</button>
      <img src={selectedImageUrl} alt={selectedImageName} />
      <p class="modal-caption">{selectedImageName}</p>
    </div>
  </div>
{/if}

<!-- Report Modal Instance -->
{#if currentUser}
  <ReportModal 
    bind:showModal={showReportModal} 
    targetId={reportTargetId} 
    targetType="product" 
    userId={currentUser.idTokenClaims.oid}
    on:success={handleReportSuccess}
  />
{/if}

<div class="search-container">
  <h2>Search Product Discounts ({$selectedCountry})</h2>
  <form on:submit|preventDefault={() => handleSearch(true)} class="search-form">
    <input type="text" bind:value={productName} placeholder="Product Name" />
    <input type="text" bind:value={storeName} placeholder="Store Name" />
    <input type="text" bind:value={barcode} placeholder="Barcode" />
    <button type="submit" disabled={isLoading}>Search</button>
  </form>

  {#if error} <p class="error">{error}</p> {/if}
  {#if isLoading && results.length === 0} <p>Loading...</p> {/if}

  <div class="results-list">
    {#each results as item (item._id)}
      <div class="product-card">
        <div class="card-header">
            <h4>{item.productName}</h4>
            {#if currentUser && item.uploadedByUserId === currentUser.idTokenClaims.oid}
                <button class="delete-icon" title="Delete" on:click={() => handleDelete(item._id)}>&times;</button>
            {/if}
        </div>
        
        <p><strong>Store:</strong> {item.storeName}</p>
        <p><strong>Price:</strong>
          {#if item.originalPrice && item.discountPrice}
            <span class="orig-price">${item.originalPrice.toFixed(2)}</span>
            <span class="disc-price">${item.discountPrice.toFixed(2)}</span>
          {:else if item.originalPrice}
            <span>${item.originalPrice.toFixed(2)}</span>
          {/if}
        </p>
        {#if item.discountPercentage}
          <p class="percent-off">{item.discountPercentage.toFixed(2)}% OFF</p>
        {/if}
        {#if item.description}
          <p><strong>Offer:</strong> {item.description}</p>
        {/if}

        {#if item.imageUrl}
          <button class="view-photo-btn" on:click={() => openImageModal(item.imageUrl, item.productName)}>
            📷 View Photo
          </button>
        {/if}
        
        <div class="card-footer">
            <div class="footer-info">
                <div class="author-text">
                    Last modified by <strong>{item.uploadedByUserName || 'Unknown'}</strong>
                    <span class="time-ago">• {getRelativeTime(item.lastUpdated)}</span>
                </div>
                <div class="stats">
                    <span title="Co-authors">👥 {item.noOfCoAuthors || 1}</span>
                    <button class="like-btn" on:click={() => handleLike(item._id)}>
                        ❤️ {item.likeCount || 0}
                    </button>
                    <!-- Issue Report Button -->
                    <button class="like-btn warning" title="Report Issue" on:click={() => openReportModal(item._id)}>
                        ⚠️ {item.issueCount || 0}
                    </button>
                </div>
            </div>
            <Link to={`/products/edit/${item._id}`} class="edit-btn">Edit</Link>
        </div>
      </div>
    {/each}
  </div>
  
  {#if hasMore}
      <div class="load-more-container">
        <button class="load-more-btn" on:click={loadMore} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Load More'}
        </button>
      </div>
  {/if}

</div>

<style>
  .search-container { max-width: 900px; margin: 2rem auto; padding: 0 1rem; }
  
  /* Mobile-first: Stack inputs vertically by default */
  .search-form { 
    display: grid; 
    grid-template-columns: 1fr; 
    gap: 1rem; 
    margin-bottom: 2rem; 
  }

  /* Tablet and up: Switch to a horizontal layout */
  @media (min-width: 768px) {
    .search-form {
      grid-template-columns: 3fr 3fr 2fr 1fr;
    }
  }

  input { padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; }
  button { padding: 0.75rem; background-color: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; }
  
  .results-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1rem; }
  
  .product-card { background: #fff; padding: 1rem; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); position: relative; }
  
  .card-header { display: flex; justify-content: space-between; align-items: flex-start; }
  .card-header h4 { margin: 0; margin-bottom: 0.5rem; }
  
  .delete-icon { background: none; color: #dc3545; font-size: 1.5rem; padding: 0; line-height: 1; width: auto; }
  .delete-icon:hover { background: none; color: #a71d2a; }

  .orig-price { text-decoration: line-through; color: #6c757d; margin-right: 0.5rem; }
  .disc-price { font-weight: bold; color: #c52d3a; }
  .percent-off { font-weight: bold; color: #28a745; font-size: 1.2rem; }
  
  .view-photo-btn {
      background: none; color: #007bff; border: 1px solid #007bff;
      padding: 5px 10px; font-size: 0.9rem; border-radius: 4px;
      margin: 0.5rem 0; width: auto;
  }
  .view-photo-btn:hover { background-color: #f0f8ff; }

  .card-footer { 
      display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap;
      margin-top: 1rem; padding-top: 0.5rem; border-top: 1px solid #eee; 
  }
  .footer-info { display: flex; flex-direction: column; font-size: 0.8rem; color: #6c757d; }
  .stats { display: flex; gap: 1rem; align-items: center; margin-top: 0.3rem; }
  .time-ago { font-style: italic; }
  
  .like-btn { background: none; color: #333; padding: 2px 6px; width: auto; border: 1px solid #eee; font-size: 0.9rem; }
  .like-btn:hover { background-color: #fff0f0; border-color: #ffcccc; }
  .like-btn.warning:hover { background-color: #fff3cd; border-color: #ffecb5; color: #856404; }

  .edit-btn { text-decoration: none; font-size: 0.9rem; color: #007bff; }
  .error { color: red; text-align: center; }
  .load-more-container { text-align: center; margin-top: 2rem; }
  .load-more-btn { background-color: #f8f9fa; color: #333; border: 1px solid #ccc; padding: 0.75rem 2rem; font-size: 1rem; transition: background 0.2s; border-radius: 4px; }
  .load-more-btn:hover { background-color: #e2e6ea; }

  /* Modal Styles */
  .modal-backdrop { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.8); display: flex; justify-content: center; align-items: center; z-index: 2000; }
  .image-modal-content { background: transparent; padding: 0; box-shadow: none; text-align: center; max-width: 90%; max-height: 90vh; position: relative; }
  .image-modal-content img { max-width: 100%; max-height: 80vh; border-radius: 4px; box-shadow: 0 5px 15px rgba(0,0,0,0.5); }
  .close-modal-btn { position: absolute; top: -40px; right: 0; background: none; border: none; color: white; font-size: 2rem; cursor: pointer; padding: 0; }
  .modal-caption { color: white; margin-top: 1rem; font-size: 1.1rem; }
</style>